import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Check, Download } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    openai_api_key: '',
    gmail_client_id: '',
    gmail_client_secret: '',
  });

  useEffect(() => {
    fetchSettings();
    fetchHealth();
    
    // Check OAuth callback
    const oauthStatus = searchParams.get('oauth');
    if (oauthStatus === 'success') {
      alert('Gmail connected successfully!');
      window.history.replaceState({}, '', '/settings');
    } else if (oauthStatus === 'error') {
      alert(`OAuth error: ${searchParams.get('message') || 'Unknown error'}`);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      if (response.data.openai_api_key) {
        setFormData(prev => ({ ...prev, openai_api_key: '••••••••••••••••' }));
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setHealth(response.data);
    } catch (err) {
      console.error('Health check failed:', err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const updateData = {};
      
      // Only include if user changed it (not the masked value)
      if (formData.openai_api_key && !formData.openai_api_key.includes('•')) {
        updateData.openai_api_key = formData.openai_api_key;
      }
      
      await axios.put(`${API}/settings`, updateData);
      alert('Settings saved successfully!');
      fetchSettings();
      fetchHealth();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGmailOAuth = async () => {
    if (!formData.gmail_client_id || !formData.gmail_client_secret) {
      alert('Please enter Gmail Client ID and Client Secret');
      return;
    }
    
    try {
      setOauthLoading(true);
      const response = await axios.post(`${API}/auth/gmail/init`, {
        client_id: formData.gmail_client_id,
        client_secret: formData.gmail_client_secret,
        redirect_uri: `${BACKEND_URL}/api/auth/gmail/callback`
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to initiate OAuth');
      setOauthLoading(false);
    }
  };

  const handleExportWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/n8n/workflows`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'n8n_workflows.json';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export workflows');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Configure your Gmail automation</p>
      </div>

      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle>Connection Status</CardTitle>
          <CardDescription>Check your integration status</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span>Gmail OAuth</span>
            {health?.gmail_configured ? (
              <div className="flex items-center text-green-600">
                <Check className="h-4 w-4 mr-2" />
                Connected
              </div>
            ) : (
              <div className="text-red-600">Not Connected</div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span>OpenAI API</span>
            {health?.openai_configured ? (
              <div className="flex items-center text-green-600">
                <Check className="h-4 w-4 mr-2" />
                Configured
              </div>
            ) : (
              <div className="text-red-600">Not Configured</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* OpenAI API Key */}
      <Card>
        <CardHeader>
          <CardTitle>OpenAI API Key</CardTitle>
          <CardDescription>Required for AI email classification</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="openai_key">API Key</Label>
            <Input
              id="openai_key"
              type="password"
              placeholder="sk-..."
              value={formData.openai_api_key}
              onChange={(e) => setFormData({ ...formData, openai_api_key: e.target.value })}
            />
          </div>
          <Button onClick={handleSaveSettings} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save API Key'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Gmail OAuth */}
      <Card>
        <CardHeader>
          <CardTitle>Gmail OAuth Configuration</CardTitle>
          <CardDescription>
            Set up Gmail OAuth credentials from Google Cloud Console
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              1. Go to Google Cloud Console<br />
              2. Create OAuth 2.0 credentials<br />
              3. Add redirect URI: {BACKEND_URL}/api/auth/gmail/callback<br />
              4. Enter credentials below and connect
            </AlertDescription>
          </Alert>
          
          <div>
            <Label htmlFor="client_id">Client ID</Label>
            <Input
              id="client_id"
              placeholder="Your Google OAuth Client ID"
              value={formData.gmail_client_id}
              onChange={(e) => setFormData({ ...formData, gmail_client_id: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="client_secret">Client Secret</Label>
            <Input
              id="client_secret"
              type="password"
              placeholder="Your Google OAuth Client Secret"
              value={formData.gmail_client_secret}
              onChange={(e) => setFormData({ ...formData, gmail_client_secret: e.target.value })}
            />
          </div>
          
          <Button onClick={handleGmailOAuth} disabled={oauthLoading}>
            {oauthLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Gmail'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* n8n Export */}
      <Card>
        <CardHeader>
          <CardTitle>n8n Workflow Export</CardTitle>
          <CardDescription>
            Download workflow JSON files to import into n8n
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleExportWorkflows} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export n8n Workflows
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            After exporting, import the workflows into your n8n instance and configure credentials.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;